"use client";

import { useState, useMemo, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { AssetType } from '@/services/asset-service';

type Props = {
    availableTypes: AssetType[];
    onFilterChange: (selectedTypes: string[] | undefined) => void;
    searchQuery?: string;
    onSearchChange?: (searchQuery: string) => void;
    includePublic?: boolean;
    onIncludePublicChange?: (includePublic: boolean) => void;
};

export function AssetFilterControls({
    availableTypes,
    onFilterChange,
    searchQuery = '',
    onSearchChange,
    includePublic = false,
    onIncludePublicChange
}: Props) {
    const [selectedCategory, setSelectedCategory] = useState('All');

    // --- DYNAMIC CATEGORY GENERATION ---
    const { assetCategories, categoryList } = useMemo(() => {
        if (availableTypes.length === 0) {
            return { assetCategories: {}, categoryList: ['All'] };
        }

        const categories: Record<string, string[]> = {};
        for (const type of availableTypes) {
            const [categoryKey] = type.name.split('/');
            if (categoryKey) {
                const formattedCategory = categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
                if (!categories[formattedCategory]) {
                    categories[formattedCategory] = [];
                }
                categories[formattedCategory].push(type.name);
            }
        }

        const derivedCategoryList = ['All', ...Object.keys(categories).sort()];
        return { assetCategories: categories, categoryList: derivedCategoryList };
    }, [availableTypes]);

    // --- EFFECT TO NOTIFY PARENT OF CHANGES ---
    useEffect(() => {
        // When the selected category changes, calculate the new list of types and notify the parent.
        const typesToFetch = selectedCategory === 'All' ? undefined : assetCategories[selectedCategory];
        onFilterChange(typesToFetch);
    }, [selectedCategory, assetCategories, onFilterChange]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange?.(e.target.value);
    };

    const handlePublicToggle = (checked: boolean) => {
        onIncludePublicChange?.(checked);
    };

    return (
        <div className="flex flex-col gap-3 w-full">
            <div className="flex flex-col sm:flex-row gap-3 w-full items-start sm:items-center">
                <div className="flex flex-wrap items-center gap-2 flex-1">
                    {categoryList.map(category => (
                        <Button
                            key={category}
                            variant={selectedCategory === category ? 'secondary' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedCategory(category)}
                            className="rounded-full px-4"
                        >
                            {category}
                        </Button>
                    ))}
                </div>

                <div className="w-full sm:w-auto max-w-xs">
                    <Input
                        placeholder="Search assets..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Checkbox
                    id="include-public-assets"
                    checked={includePublic}
                    onCheckedChange={handlePublicToggle}
                />
                <Label
                    htmlFor="include-public-assets"
                    className="text-sm font-medium cursor-pointer"
                >
                    Show public assets
                </Label>
            </div>
        </div>
    );
}